import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { apiRequest } from "../context/AuthContext";
import { useTheme, DIVISION_THEME } from "../context/ThemeContext";
import { getCollegeImage } from "../utils/collegeImages";
import { Search, MapPin, Users, Plus, Check, Flag, BarChart2, ArrowLeft, ShieldCheck, Mail, MailX, Zap, PenLine } from "lucide-react";

export default function CollegesPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const trackedOnly = searchParams.get("view") === "tracked";
  const { division: basketballDivision } = useTheme();
  const theme = DIVISION_THEME[basketballDivision] || DIVISION_THEME.mens;
  const [colleges, setColleges] = useState([]);
  const [allColleges, setAllColleges] = useState([]);
  const [tracked, setTracked] = useState(new Set());
  const [trackedData, setTrackedData] = useState({});
  const [compareSet, setCompareSet] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [ncaaDivision, setNcaaDivision] = useState("");
  const [foreignOnly, setForeignOnly] = useState(false);
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [state, setState] = useState("");
  const [contactFilter, setContactFilter] = useState("all"); // "all" | "contacted" | "not_contacted"
  const [repliedInstitutions, setRepliedInstitutions] = useState(new Set());
  const [notRepliedInstitutions, setNotRepliedInstitutions] = useState(new Set());
  const [lateOpenings, setLateOpenings] = useState([]);
  const [lateOpeningsActive, setLateOpeningsActive] = useState(false);
  const PAGE_SIZE = 40;
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  useEffect(() => {
    fetchAllColleges();
    fetchTracked();
    fetchContactedInstitutions();
    fetchLateOpenings();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [search, ncaaDivision, foreignOnly, verifiedOnly, state, allColleges, trackedOnly, trackedData, tracked, basketballDivision, contactFilter, repliedInstitutions, notRepliedInstitutions]); // eslint-disable-line

  const fetchAllColleges = async () => {
    try {
      const { data } = await apiRequest("get", "/colleges");
      setAllColleges(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = allColleges;
    // trackedOnly is a pipeline view — only apply it when not using discovery filters
    if (trackedOnly && !verifiedOnly && !foreignOnly) filtered = filtered.filter(c => tracked.has(c.id));
    // Filter by basketball program gender (graceful: null/undefined = show for all)
    filtered = filtered.filter(c => {
      if (!c.program_gender) return true;
      if (basketballDivision === "womens") return c.program_gender === "womens" || c.program_gender === "both";
      return c.program_gender === "mens" || c.program_gender === "both";
    });
    if (search) filtered = filtered.filter(c => c.name.toLowerCase().includes(search.toLowerCase()) || c.location.toLowerCase().includes(search.toLowerCase()) || c.country?.toLowerCase().includes(search.toLowerCase()));
    if (ncaaDivision) filtered = filtered.filter(c => c.division === ncaaDivision);
    if (foreignOnly) filtered = filtered.filter(c => c.foreign_friendly);
    if (verifiedOnly) filtered = filtered.filter(c => (c.coaches || []).some(coach => coach.last_verified));
    if (state) filtered = filtered.filter(c => c.state?.toLowerCase().includes(state.toLowerCase()) || c.location?.toLowerCase().includes(state.toLowerCase()) || c.country?.toLowerCase().includes(state.toLowerCase()));
    // Contact status filter
    if (contactFilter === "contacted") {
      filtered = filtered.filter(c => matchesInstitutionSet(c, repliedInstitutions));
    } else if (contactFilter === "not_contacted") {
      filtered = filtered.filter(c => matchesInstitutionSet(c, notRepliedInstitutions));
    }
    setColleges(filtered);
    setVisibleCount(PAGE_SIZE);
  };

  const fetchTracked = async () => {
    try {
      const { data } = await apiRequest("get", "/my-colleges");
      setTracked(new Set(data.map(t => t.college_id)));
      const map = {};
      data.forEach(t => { map[t.college_id] = t; });
      setTrackedData(map);
    } catch {}
  };

  const fetchContactedInstitutions = async () => {
    try {
      const { data } = await apiRequest("get", "/player/contacted-institutions");
      const toSet = (arr) => new Set((arr || []).map(n => n.toLowerCase().trim()));
      setRepliedInstitutions(toSet(data.replied));
      setNotRepliedInstitutions(toSet(data.not_replied));
    } catch {}
  };

  const fetchLateOpenings = async () => {
    try {
      const { data } = await apiRequest("get", "/colleges/late-openings");
      setLateOpenings(data || []);
    } catch {}
  };

  // Fuzzy-match college name against a Set of institution name strings
  const matchesInstitutionSet = (college, nameSet) => {
    const cName = (college.name || "").toLowerCase().trim();
    for (const inst of nameSet) {
      if (inst === cName || cName.includes(inst) || inst.includes(cName)) return true;
    }
    return false;
  };

  const toggleTrack = async (e, college) => {
    e.stopPropagation();
    if (tracked.has(college.id)) {
      await apiRequest("delete", `/my-colleges/${college.id}`);
      setTracked(prev => { const s = new Set(prev); s.delete(college.id); return s; });
      setTrackedData(prev => { const d = { ...prev }; delete d[college.id]; return d; });
    } else {
      await apiRequest("post", "/my-colleges", { college_id: college.id, notes: "" });
      setTracked(prev => new Set([...prev, college.id]));
      setTrackedData(prev => ({ ...prev, [college.id]: { status: "interested", progress_score: 10 } }));
    }
  };

  const toggleCompare = (e, college) => {
    e.stopPropagation();
    setCompareSet(prev => {
      const s = new Set(prev);
      if (s.has(college.id)) { s.delete(college.id); }
      else if (s.size < 3) { s.add(college.id); }
      return s;
    });
  };

  const startComparison = () => {
    if (compareSet.size >= 2) navigate(`/compare?ids=${[...compareSet].join(",")}`);
  };

  const divisions = ["Division I", "Division II", "NAIA", "JUCO"];

  const scoreColor = (s) => s >= 75 ? "#10b981" : s >= 50 ? "#f97316" : s >= 25 ? "#3b82f6" : "#94a3b8";
  const scoreBg = (s) => s >= 75 ? "bg-emerald-500" : s >= 50 ? "bg-orange-500" : s >= 25 ? "bg-blue-500" : "bg-slate-400";

  return (
    <div style={{ fontFamily: "Manrope, sans-serif" }}>
      <div className="mb-6">
        {trackedOnly ? (
          <>
            <button
              onClick={() => navigate("/colleges")}
              className="flex items-center gap-1.5 text-slate-500 hover:text-slate-800 text-sm mb-2 transition-colors"
              data-testid="back-to-all-colleges"
            >
              <ArrowLeft className="w-4 h-4" /> All Colleges
            </button>
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-orange-600">Your Pipeline</span>
            <h1 className="text-3xl font-bold text-slate-900 mt-1" style={{ fontFamily: "Barlow Condensed, sans-serif", textTransform: "uppercase" }}>
              My Tracked Colleges
            </h1>
            <p className="text-slate-500 mt-1">
              {tracked.size > 0 ? `${tracked.size} college${tracked.size !== 1 ? "s" : ""} in your list` : "No colleges tracked yet"}
            </p>
          </>
        ) : (
          <>
            <span className="text-xs tracking-[0.2em] uppercase font-bold" style={{ color: theme.accentText }}>
              {basketballDivision === "womens" ? "Women's Division" : "College Directory"}
            </span>
            <h1 className="text-3xl font-bold text-slate-900 mt-1" style={{ fontFamily: theme.fontHeading, textTransform: "uppercase" }}>
              Find Your College
            </h1>
            <p className="text-slate-500 mt-1">Browse {allColleges.length} colleges with basketball programs</p>
          </>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-lg p-4 mb-6 space-y-3">
        {/* Late Openings tab — only rendered when at least one college has a live roster signal */}
        {lateOpenings.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap">
            <button
              data-testid="late-openings-tab"
              onClick={() => setLateOpeningsActive(v => !v)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider transition-all border ${
                lateOpeningsActive
                  ? "bg-red-500 text-white border-red-500 shadow-sm"
                  : "bg-white text-red-600 border-red-300 hover:border-red-500 hover:bg-red-50"
              }`}
            >
              <Zap className="w-3.5 h-3.5" />
              Late Openings
              <span className={`ml-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-extrabold ${lateOpeningsActive ? "bg-white/30 text-white" : "bg-red-100 text-red-600"}`}>
                {lateOpenings.length}
              </span>
            </button>
            {lateOpeningsActive && (
              <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-1.5 flex items-center gap-1.5">
                <Zap className="w-3 h-3" />
                Showing {lateOpenings.length} college{lateOpenings.length !== 1 ? "s" : ""} with a confirmed or likely roster opening — act fast and compose now.
              </p>
            )}
          </div>
        )}

        {/* Contact Status Toggle */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-bold text-slate-500 uppercase tracking-wider mr-1">Contact Status:</span>
          {[
            { key: "all",           label: "All Colleges",      icon: null },
            { key: "contacted",     label: `Contacted${repliedInstitutions.size > 0 ? ` (${repliedInstitutions.size})` : ""}`,     icon: Mail },
            { key: "not_contacted", label: `Not Contacted${notRepliedInstitutions.size > 0 ? ` (${notRepliedInstitutions.size})` : ""}`, icon: MailX },
          ].map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              data-testid={`contact-filter-${key}`}
              onClick={() => setContactFilter(key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all border ${
                contactFilter === key
                  ? key === "contacted"
                    ? "bg-green-500 text-white border-green-500"
                    : key === "not_contacted"
                    ? "bg-orange-500 text-white border-orange-500"
                    : "bg-slate-800 text-white border-slate-800"
                  : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
              }`}
            >
              {Icon && <Icon className="w-3.5 h-3.5" />}
              {label}
            </button>
          ))}
          {contactFilter !== "all" && (
            <button
              onClick={() => setContactFilter("all")}
              className="text-xs text-slate-400 hover:text-slate-600 ml-1"
              data-testid="contact-filter-clear"
            >
              Clear
            </button>
          )}
        </div>

        {/* Hint text for contact filters */}
        {contactFilter === "contacted" && (
          <p className="text-xs text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
            Showing colleges you have already replied to — you have contacted {repliedInstitutions.size} college{repliedInstitutions.size !== 1 ? "s" : ""}.
          </p>
        )}
        {contactFilter === "not_contacted" && (
          <p className="text-xs text-orange-700 bg-orange-50 border border-orange-200 rounded-lg px-3 py-2">
            Showing colleges whose coaches have messaged you but you haven't replied to yet — {notRepliedInstitutions.size} college{notRepliedInstitutions.size !== 1 ? "s" : ""} awaiting your reply.
          </p>
        )}
        {/* Search + Division filter row */}
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-48">
            <Search className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
            <input
              data-testid="college-search-input"
              type="text"
              placeholder="Search colleges, cities, countries..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
            />
          </div>
          <select
            data-testid="college-division-filter"
            value={ncaaDivision}
            onChange={e => setNcaaDivision(e.target.value)}
            className="border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-orange-500 outline-none text-slate-700 bg-white"
          >
            <option value="">All Divisions / Leagues</option>
            {divisions.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
          <input
            data-testid="college-state-filter"
            type="text"
            placeholder="State..."
            value={state}
            onChange={e => setState(e.target.value)}
            className="border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-orange-500 outline-none w-28"
          />
          <button
            data-testid="college-foreign-filter"
            onClick={() => setForeignOnly(!foreignOnly)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-bold uppercase tracking-wider border-2 transition-all ${foreignOnly ? "bg-green-500 text-white border-green-500 shadow-sm" : "bg-white text-green-700 border-green-300 hover:bg-green-50"}`}
          >
            <Flag className="w-3.5 h-3.5" />
            UK Friendly {foreignOnly && `(${colleges.length})`}
          </button>
          <button
            data-testid="college-verified-filter"
            onClick={() => setVerifiedOnly(!verifiedOnly)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-bold uppercase tracking-wider border-2 transition-all ${verifiedOnly ? "bg-blue-600 text-white border-blue-600 shadow-sm" : "bg-white text-blue-700 border-blue-300 hover:bg-blue-50"}`}
          >
            <ShieldCheck className="w-3.5 h-3.5" />
            Email Verified {verifiedOnly && `(${colleges.length})`}
          </button>
          {(search || ncaaDivision || foreignOnly || verifiedOnly || state) && (
            <button
              onClick={() => { setSearch(""); setNcaaDivision(""); setForeignOnly(false); setVerifiedOnly(false); setState(""); }}
              className="px-3 py-2.5 text-xs text-slate-500 hover:text-slate-700 font-medium border border-slate-200 rounded-lg hover:bg-slate-50 transition-all"
            >
              Clear filters
            </button>
          )}
        </div>
        {foreignOnly && (
          <div className="mt-3 flex items-center gap-2 text-xs text-green-700 bg-green-50 rounded-lg px-3 py-2 border border-green-200">
            <Flag className="w-3.5 h-3.5" />
            Showing {colleges.length} colleges that actively recruit UK & international players — great targets for you as an England U18 player.
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-10 w-10 border-4 border-orange-500 border-t-transparent" />
        </div>
      ) : colleges.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center">
          <Search className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          {trackedOnly ? (
            <>
              <p className="text-slate-600 font-semibold text-base mb-1">No colleges in your list yet</p>
              <p className="text-slate-400 text-sm mb-4">Browse the full directory and click "Add to My List" on any college to start tracking.</p>
              <button onClick={() => navigate("/colleges")} className="bg-orange-500 text-white font-bold uppercase tracking-wider rounded-lg px-5 py-2.5 text-sm hover:bg-orange-600 transition-colors">
                Browse All Colleges
              </button>
            </>
          ) : (
            <>
              <p className="text-slate-500 font-medium">No colleges match your filters.</p>
              <button onClick={() => { setSearch(""); setNcaaDivision(""); setForeignOnly(false); setState(""); }} style={{ color: theme.accent }} className="mt-3 font-semibold text-sm">
                Clear all filters
              </button>
            </>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {(lateOpeningsActive ? lateOpenings : colleges.slice(0, visibleCount)).map((college) => (
            <div
              key={college.id}
              data-testid={`college-card-${college.name}`}
              className={`bg-white border rounded-lg hover:shadow-md transition-all duration-200 hover:-translate-y-0.5 cursor-pointer overflow-hidden ${compareSet.has(college.id) ? "border-purple-400 ring-2 ring-purple-300" : lateOpeningsActive ? "border-red-200" : college.foreign_friendly ? "border-green-200" : "border-slate-200"}`}
              onClick={() => navigate(`/colleges/${college.id}`)}
            >
              <div className="h-32 relative overflow-hidden">
                <img
                  src={getCollegeImage(college.name, college.image_url)}
                  alt={college.name}
                  className="w-full h-full object-cover"
                  loading="lazy"
                  onError={e => { e.target.src = getCollegeImage("default"); }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent" />
                <div className="absolute top-3 right-3 flex gap-1.5 flex-wrap justify-end">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-bold uppercase tracking-wide ${
                    college.division === "Division I" ? "bg-orange-500 text-white" :
                    college.division === "Division II" ? "bg-blue-500 text-white" :
                    college.division === "NAIA" ? "bg-purple-500 text-white" :
                    college.division === "JUCO" ? "bg-green-600 text-white" :
                    "bg-slate-700 text-white"
                  }`}>
                    {college.division?.replace("Division ", "D")}
                  </span>
                  {college.foreign_friendly && (
                    <span className="px-2 py-0.5 rounded-full text-xs font-bold uppercase tracking-wide bg-green-500 text-white flex items-center gap-1">
                      <Flag className="w-2.5 h-2.5" /> UK Pick
                    </span>
                  )}
                  {/* Late opening badge — shown in all views when status is set */}
                  {(college.late_opening_status === "confirmed_open" || college.late_opening_status === "likely_open") && (
                    <span
                      data-testid={`late-opening-badge-${college.id}`}
                      className={`px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase tracking-wide flex items-center gap-1 ${
                        college.late_opening_status === "confirmed_open"
                          ? "bg-red-500 text-white"
                          : "bg-amber-500 text-white"
                      }`}
                    >
                      <Zap className="w-2.5 h-2.5" />
                      {college.late_opening_status === "confirmed_open" ? "Open" : "Likely Open"}
                    </span>
                  )}
                  {!lateOpeningsActive && matchesInstitutionSet(college, repliedInstitutions) && (
                    <span className="px-2 py-0.5 rounded-full text-xs font-bold uppercase tracking-wide bg-green-600 text-white flex items-center gap-1" data-testid={`contacted-badge-${college.id}`}>
                      <Mail className="w-2.5 h-2.5" /> Contacted
                    </span>
                  )}
                  {!lateOpeningsActive && matchesInstitutionSet(college, notRepliedInstitutions) && (
                    <span className="px-2 py-0.5 rounded-full text-xs font-bold uppercase tracking-wide bg-orange-500 text-white flex items-center gap-1" data-testid={`not-contacted-badge-${college.id}`}>
                      <MailX className="w-2.5 h-2.5" /> Reply Pending
                    </span>
                  )}
                </div>
                {/* Compare toggle */}
                <button
                  data-testid={`compare-toggle-${college.name}`}
                  onClick={(e) => toggleCompare(e, college)}
                  className={`absolute top-3 left-3 w-7 h-7 rounded-lg flex items-center justify-center transition-all text-xs font-black ${compareSet.has(college.id) ? "bg-purple-500 text-white shadow-lg" : compareSet.size >= 3 ? "bg-black/30 text-white/40 cursor-not-allowed" : "bg-black/30 text-white hover:bg-purple-500"}`}
                  title={compareSet.has(college.id) ? "Remove from comparison" : compareSet.size >= 3 ? "Max 3 colleges" : "Add to comparison"}
                >
                  {compareSet.has(college.id) ? <Check className="w-3.5 h-3.5" /> : <BarChart2 className="w-3.5 h-3.5" />}
                </button>
                <div className="absolute bottom-3 left-3">
                  <span className="text-xs text-white/80 font-medium">{college.conference}</span>
                </div>
                {/* Progress score badge (only for tracked colleges) */}
                {trackedData[college.id] && (
                  <div
                    data-testid={`progress-badge-${college.name}`}
                    className={`absolute bottom-3 right-3 text-white text-xs font-black px-2 py-0.5 rounded-full ${scoreBg(trackedData[college.id].progress_score ?? 0)}`}
                  >
                    {trackedData[college.id].progress_score ?? 0}%
                  </div>
                )}
              </div>
              <div className="p-4">
                <div className="min-w-0 mb-3">
                  <h3 className="font-bold text-slate-900 text-sm truncate">{college.name}</h3>
                  <div className="flex items-center gap-1 mt-1">
                    <MapPin className="w-3 h-3 text-slate-400" />
                    <span className="text-xs text-slate-500">{college.location}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-xs text-slate-500 mb-2 flex-wrap">
                  <div className="flex items-center gap-1">
                    <Users className="w-3 h-3" />
                    <span>{(college.coaches || []).filter(c => c.programme_gender === basketballDivision).length} coaches</span>
                  </div>
                  {college.foreign_friendly && (
                    <span className="text-green-600 font-semibold flex items-center gap-0.5">
                      <Flag className="w-3 h-3" /> UK
                    </span>
                  )}
                </div>
                {/* Late Openings view: primary Compose button + secondary Track */}
                {lateOpeningsActive ? (
                  <div className="flex gap-2">
                    <button
                      data-testid={`compose-late-opening-${college.id}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate("/compose", { state: { college } });
                      }}
                      className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg font-bold uppercase tracking-wider text-xs bg-red-500 hover:bg-red-600 text-white transition-all"
                    >
                      <PenLine className="w-3.5 h-3.5" /> Compose
                    </button>
                    <button
                      data-testid={`track-college-${college.name}`}
                      onClick={(e) => toggleTrack(e, college)}
                      className={`px-3 py-2 rounded-lg font-bold uppercase tracking-wider text-xs transition-all border ${
                        tracked.has(college.id)
                          ? "bg-green-50 text-green-700 border-green-300 hover:bg-red-50 hover:text-red-600 hover:border-red-300"
                          : "bg-slate-100 text-slate-600 border-slate-200 hover:bg-slate-200"
                      }`}
                    >
                      {tracked.has(college.id) ? <Check className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                ) : (
                  /* Normal view: full-width Track button */
                  <button
                    data-testid={`track-college-${college.name}`}
                    onClick={(e) => toggleTrack(e, college)}
                    className={`w-full flex items-center justify-center gap-2 py-2 rounded-lg font-bold uppercase tracking-wider text-xs transition-all ${
                      tracked.has(college.id)
                        ? "bg-green-50 text-green-700 border-2 border-green-300 hover:bg-red-50 hover:text-red-600 hover:border-red-300"
                        : "text-white"
                    }`}
                    style={!tracked.has(college.id) ? { background: theme.accent, borderRadius: `var(--btn-radius, 0.5rem)` } : {}}
                  >
                    {tracked.has(college.id) ? <><Check className="w-3.5 h-3.5" /> Tracking</> : <><Plus className="w-3.5 h-3.5" /> Add to My List</>}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Load More — hidden when Late Openings tab active (full set already shown) */}
      {!loading && !lateOpeningsActive && colleges.length > visibleCount && (
        <div className="flex justify-center mt-6 mb-2">
          <button
            data-testid="load-more-colleges-btn"
            onClick={() => setVisibleCount(v => v + PAGE_SIZE)}
            className="px-6 py-2.5 rounded-xl font-semibold text-sm text-white transition-all hover:opacity-90"
            style={{ background: theme.accent }}
          >
            Load more ({colleges.length - visibleCount} remaining)
          </button>
        </div>
      )}

      {/* Sticky Compare Bar */}
      {compareSet.size > 0 && (
        <div
          data-testid="compare-sticky-bar"
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-slate-900 text-white rounded-2xl shadow-2xl px-5 py-3.5 flex items-center gap-4 border border-white/10"
          style={{ backdropFilter: "blur(12px)" }}
        >
          <div className="flex items-center gap-2">
            <BarChart2 className="w-4 h-4 text-purple-400" />
            <span className="text-sm font-bold">{compareSet.size} selected</span>
            <span className="text-white/40 text-xs">(max 3)</span>
          </div>
          <div className="h-4 w-px bg-white/20" />
          <button
            data-testid="clear-compare-btn"
            onClick={() => setCompareSet(new Set())}
            className="text-xs text-white/50 hover:text-white transition-colors"
          >
            Clear
          </button>
          <button
            data-testid="start-compare-btn"
            onClick={startComparison}
            disabled={compareSet.size < 2}
            className="bg-purple-500 hover:bg-purple-600 text-white font-bold text-sm px-4 py-1.5 rounded-xl transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {compareSet.size < 2 ? `Select ${2 - compareSet.size} more` : "Compare →"}
          </button>
        </div>
      )}

    </div>
  );
}
